import { type yup } from '@framework/validation/initYup.ts';
import { getAllRoots } from '../../../tree/references/utils/getAllRoots.ts';
import {
  extractAllCodesMap,
  type IConfigValidationContext,
} from './hooks/useConfigValidationContext.ts';
import { widgetCodeSchema } from './widgetCodeSchema.tsx';

/**
 * Validatore che, attraverso il context passato al form, permette di verificare che il codice sia univoco.
 */
export const uniqueWidgetCodeSchema = widgetCodeSchema.test(
  '$allCodes',
  ({ label, value }) => `${String(label)} "${String(value)}" già in uso.`,
  (value, testContext) => {
    // Se il valore è vuoto, non è necessario controllare che sia univoco.
    if (!value) return true;

    const allCodes = getAllCodes(testContext);

    // Se non è presente il contesto, non svolgo il test.
    if (!allCodes) {
      return true;
    }

    const parentId = testContext.parent.id as string | undefined;

    // Se il parent id non è definito, loggo un warning.
    // `id: yup.string();` deve essere definito nel validatore così che sia visibile dal context.
    // È necessario per poter capire se il codice è già in uso da _un diverso widget_.
    if (!parentId) {
      // biome-ignore format: <tbd>
      console.warn(`[uniqueWidgetCodeValidator] parent.id undefined. Assicurarsi che "id" sia presente nel validatore.`, {parentId, context: testContext});
    }

    const existingWidgetsId = allCodes.get(value);

    // O il codice non è presente, o è presente ma è lo stesso widget.
    return (
      !existingWidgetsId ||
      existingWidgetsId.length === 0 ||
      (existingWidgetsId.length === 1 && existingWidgetsId[0] === parentId)
    );
  },
);

/**
 * Estrae dinamicamente la mappa dei codici dei widget durante la validazione.
 * Utilizzato quando non è possibile passare la mappa dei codici dei widget nel contesto,
 * come nella validazione totale della configurazione.
 */
function getAllCodes(testContext: yup.TestContext<yup.AnyObject>) {
  const context = testContext.options.context as
    | IConfigValidationContext
    | undefined;

  if (context?.allCodes && context.allCodes.size > 0) return context.allCodes;

  // Se la validazione è partita da "sections" (e quindi dal SectionListSchema)
  // E non è stato fornita la Map già nel contesto, allora va calcolata
  if (
    testContext.path.startsWith('sections') &&
    testContext.path.includes('.') &&
    (context?.allCodes?.size === 0 || !context?.allCodes)
  ) {
    // Recupero tutte le sezioni, presenti nel value dell'ultimo validatore
    const allSections: any[] =
      testContext.from?.[testContext.from.length - 1]?.value.sections;

    // Recupero l'oggetto allRoots calcolato a partire dalle sezioni
    const allRoots = getAllRoots(allSections);

    // Utilizzo la stessa funzione usata nell'hook per estrarre i codici
    const result = extractAllCodesMap(allRoots);

    return result.allCodes;
  }

  return null;
}
